<template>
  <div>
    <div>
      <div class="h-screen w-screen flex flex-col overflow-hidden">
        <!-- Header -->
        <div v-if="nbrCurrentShowToForm !== 1" class="header-section">
          <AdminUIHeader />
        </div>

        <!-- Discrete Progress Header -->
        <div v-if="nbrCurrentShowToForm !== 1" class="progress-header">
          <div class="progress-container">
            <!-- Navigation Button Left -->
            <button
              v-if="canGoToPreviousStep()"
              class="nav-button nav-button-left"
              :disabled="isCheckingVat || isAccountCreationInProgress"
              :class="{disabled: isCheckingVat || isAccountCreationInProgress}"
              :title="t('admin-ui.previous')"
              @click="goToPreviousStep()"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                style="margin-right: 8px"
                class="nav-icon"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="nav-text">{{ t('admin-ui.previous') }}</span>
            </button>

            <!-- Progress Bar -->
            <div class="progress-track">
              <div
                class="progress-fill"
                :style="{width: getProgressPercentage() + '%'}"
              />
            </div>

            <!-- Step Indicators -->
            <div class="step-indicators">
              <div
                v-for="(step, index) in steps"
                :key="index"
                :class="{
                  'step-dot': true,
                  active: isStepActive(step.number),
                  completed: isStepCompleted(step.number),
                }"
                :title="getStepTooltip(step)"
              >
                <span v-if="isStepCompleted(step.number)" class="check-mark"
                  >✓</span
                >
                <span v-else class="step-number">{{ index + 1 }}</span>
              </div>
            </div>

            <!-- Current Step Info -->
            <div class="current-step-info">
              <span class="step-text">{{ getCurrentStepTitle() }}</span>
              <span class="step-counter">{{ getCurrentStepNumber() }}/5</span>
            </div>

            <!-- Dynamic Action Button -->
            <button
              v-if="canGoToNextStep()"
              class="action-button"
              :disabled="isCheckingVat"
              :class="{disabled: isCheckingVat}"
              :title="getNextActionTitle()"
              @click="smartGoToNextStep()"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                style="margin-right: 8px"
                class="nav-icon"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="nav-text">{{ getNextActionLabel() }}</span>
            </button>
          </div>
        </div>

        <!-- Main Content -->
        <div class="content-section">
          <!--  Signup e-mail -->
          <div v-if="nbrCurrentShowToForm === 1">
            <RegisterEmailForm
              :show-mobile-version="showMobileVersion"
              :show-banner="showBanner"
              :text-banner="textBanner"
              :show-animation-banner="showAnimationBanner"
              @sendMail="sendUserEmail"
              @close="closeBanner"
              @languageChanged="updateBannerTextForLanguage"
            />
          </div>
          <!-- Signup password-->
          <div v-if="nbrCurrentShowToForm === 2">
            <ContactInformation
              :show-mobile-version="showMobileVersion"
              :show-banner="showBanner"
              :text-banner="textBanner"
              :show-animation-banner="showAnimationBanner"
              :error-fetch-api="canGetCustomerEmail"
              :user-retrieved-data="userRetrievedData"
              :user-data="myUser"
              :trigger-validation="triggerContactValidation"
              @nextClick="checkContactForm"
              @close="closeBanner"
              @validationResult="handleContactValidationResult"
            />
          </div>
          <!--  Check VAT -->
          <div v-if="nbrCurrentShowToForm === 4">
            <CompanyCheckForm
              :nbr-of-request-api-made="nbrRequestApiCheckVat"
              :error-api="hasVatErrorAPI"
              :message-api="messageVatAPI"
              :is-checking-vat-api="isCheckingVat"
              :my-object="myUser"
              :trigger-validation="triggerVatValidation"
              :show-banner="showBanner"
              :text-banner="textBanner"
              :show-animation-banner="showAnimationBanner"
              @nextClick="CheckVatForm"
              @previousClick="showForm(2)"
              @close="closeBanner"
              @validationResult="handleVatValidationResult"
            />
          </div>
          <!--  Company information -->
          <div v-if="nbrCurrentShowToForm === 5">
            <CompanyInformationsForm
              :my-object="myUser"
              :trigger-validation="triggerCompanyValidation"
              :show-banner="showBanner"
              :text-banner="textBanner"
              :show-animation-banner="showAnimationBanner"
              @nextClick="checkCompanyInformationsForm"
              @previousClick="showForm(4)"
              @close="closeBanner"
              @validationResult="handleCompanyValidationResult"
            />
          </div>
          <div v-if="nbrCurrentShowToForm === 6">
            <RightsForm
              :my-object="myUser"
              :trigger-validation="triggerRightsValidation"
              :show-banner="showBanner"
              :text-banner="textBanner"
              :show-animation-banner="showAnimationBanner"
              @nextClick="checkRightsForm"
              @previousClick="showForm(5)"
              @close="closeBanner"
              @validationResult="handleRightsValidationResult"
            />
          </div>
          <!--  Summary -->
          <div v-if="nbrCurrentShowToForm === 7">
            <SummaryForm
              :my-object="myUser"
              @previousClick="showForm(6)"
              @accountCreationStateChange="onAccountCreationStateChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, reactive} from 'vue';
import {getFakeAccount, MyUserCompany} from './objects/UserCompany';
import {useI18n} from 'vue-i18n';
import SummaryForm from './components/section/SummaryForm.vue';
import RightsForm from './components/section/TermsAndConditionsForm.vue';
import CompanyInformationsForm from './components/section/CompanyForm.vue';
import CompanyCheckForm from './components/section/VatForm.vue';
import EmailForm from './components/section/RegisterEmailForm.vue';
import RegisterEmailForm from './components/section/RegisterEmailForm.vue';
import ContactInformation from './components/section/PersonalInformation.vue';
import WelcomePage from './components/WelcomePage.vue';
import AdminUIHeader from './components/header/AdminUIHeader.vue';
import MyFooter from './components/footer/MyFooter.vue';
import {MyLog} from './components/tools/MyLog';
import {HitDialog} from '@hit/components/src';
import {enumValues, Form} from './components/tools/Form';
import {ApiService} from './components/API/API';
import {MyHttpManager} from './components/API/HttpManager';
import axios from 'axios';

enum StateSignUP {
  goodFormatUidReceived,
  badFormatUidReceived,
  notUidReceived,
}

export default defineComponent({
  name: 'DynamicForm',
  components: {
    ContactInformation,
    RegisterEmailForm,
    HitDialog,
    WelcomePage,
    AdminUIHeader,
    MyFooter,
    EmailForm,
    CompanyInformationsForm,
    CompanyCheckForm,
    RightsForm,
    SummaryForm,
  },
  setup() {
    const {t, locale} = useI18n();
    return {t, locale};
  },
  data() {
    return {
      textBanner: '',
      showAnimationBanner: false,
      showBanner: false,
      showMobileVersion: false,
      currentTabIndex: 0,
      currentBannerMessageKey: '',
      // Used to know which part of form show on the screen
      stateListOfFormsToShow: [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ], //todo replace by router?
      nbrCurrentShowToForm: 0 as number,
      myDialog: false,
      hasVatErrorAPI: false as boolean,
      messageVatAPI: '',
      isCheckingVat: false as boolean,
      isAccountCreationInProgress: false as boolean,
      myUser: reactive<MyUserCompany>({
        usernameEmail: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        vatNumber: '',
        countryCode: '',
        designationLegal: '',
        street: '',
        houseNumber: '',
        boxNumber: '',
        postCode: '',
        city: '',
        billingEmail: '',
        phone: '',
        mobile: '',
        rightsAccepted: false,
        dateRequest: new Date(),
        checkVatNumberBeforeAddAccount: false,
        userSystemLanguage: '',
        activity: '',
        referralCode: '',
      }),
      nbrRequestApiCheckVat: 0,
      mobileView: false,
      // Final call API
      finalApiMakingRequest: false,
      finalApiTitleToShow: "Création d'un compte client",
      finalApiMessageToShow: 'La création du compte client est en cours ...',
      finalApiLabelConfirmButton: 'OK',
      finalApiShowConfirmButton: false,
      finalApiAllowToLeaveDialog: true,
      // Used to manage dialog
      dialogTimeout: 0,
      // Demo data
      fakeUser: getFakeAccount(),
      // Email part
      canGetCustomerEmail: false,
      userRetrievedData: {},
      uidCustomerReceived: '',
      // Steps configuration
      steps: [
        {number: 2, title: this.t('admin-ui.identity')},
        {number: 4, title: this.t('admin-ui.vat-verification')},
        {number: 5, title: this.t('admin-ui.company')},
        {number: 6, title: this.t('admin-ui.conditions')},
        {number: 7, title: this.t('admin-ui.summary')},
      ],
      // Track completed steps for proper navigation
      completedSteps: new Set<number>(),
      // Trigger validation flags - cleaner approach than refs
      triggerContactValidation: 0,
      triggerVatValidation: 0,
      triggerCompanyValidation: 0,
      triggerRightsValidation: 0,
    };
  },
  watch: {
    myDialog(val) {
      if (!val) return;
      setTimeout(() => (this.myDialog = false), 4000);
    },
  },
  created() {
    let url = window.location.host + window.location.pathname;

    const referral_code = window.location.search;

    try {
      const myState = this.checkReceivedURL(url, '/', 36);
      switch (myState) {
        case StateSignUP.notUidReceived:
          this.stateListOfFormsToShow[1] = true;
          this.nbrCurrentShowToForm = 1;
          break;
        case StateSignUP.badFormatUidReceived:
          this.showBanner = true;
          this.stateListOfFormsToShow[2] = true;
          this.nbrCurrentShowToForm = 2;
          this.currentBannerMessageKey =
            'admin-ui.please-click-again-on-link-in-latest-email';
          this.textBanner = this.t(
            'admin-ui.please-click-again-on-link-in-latest-email',
          );
          setTimeout(() => {
            this.showBanner = false;
          }, 6000);
          break;
        case StateSignUP.goodFormatUidReceived:
          this.stateListOfFormsToShow[2] = true;
          this.nbrCurrentShowToForm = 2;
          if (referral_code) {
            this.getEmailAddressFromAPI(
              this.uidCustomerReceived,
              referral_code,
            );
          } else {
            this.getEmailAddressFromAPI(this.uidCustomerReceived, '');
          }

          break;
      }
    } catch (e) {
      console.warn('Invalid URL format BIS');
    }
  },
  methods: {
    showForm(dataReceived: number) {
      this.isCheckingVat = false;
      if (dataReceived !== 7) {
        this.isAccountCreationInProgress = false;
      }
      this.nbrCurrentShowToForm = dataReceived;
      this.stateListOfFormsToShow.forEach((element, index) => {
        this.stateListOfFormsToShow[index] = index === dataReceived;
      });
    },
    onAccountCreationStateChange(inProgress: boolean) {
      this.isAccountCreationInProgress = inProgress;
    },
    async sendUserEmail({email = ''}) {
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('referral_code'); // récupère la valeur de 'referral_code'

      this.showBanner = true;
      this.currentBannerMessageKey = 'admin-ui.mail-delivery-in-progress';
      this.textBanner = this.t('admin-ui.mail-delivery-in-progress');
      this.showAnimationBanner = true;

      const restOfUrl = 'mail/send/' + email;
      const fullUrl = process.env.VUE_APP_ADMIN_API_BASE_URL + restOfUrl;

      const token = MyHttpManager.generateAdminApiBase64Token();

      const config = {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        params: {
          locale: this.locale,
          referral_code: referralCode,
        },
      };

      await axios
        .post(fullUrl, {}, config)
        .then(() => {
          this.currentBannerMessageKey =
            'admin-ui.mail-successfully-delivered-check-mail-box';
          this.textBanner = this.t(
            'admin-ui.mail-successfully-delivered-check-mail-box',
          );
          this.showAnimationBanner = false;
        })
        .catch((_error) => {
          this.currentBannerMessageKey =
            'admin-ui.cannot-register-mail-address';
          this.textBanner = this.t('admin-ui.cannot-register-mail-address');
          this.showAnimationBanner = false;
        });
    },
    checkContactForm({
      email = '',
      password = '',
      confirmPassword = '',
      firstName = '',
      lastName = '',
      referralCode = '',
    }) {
      Form.addContentInUserCompanyObject(this.myUser, enumValues.email, email);
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.password,
        password,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.confirmPassword,
        confirmPassword,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.firstName,
        firstName,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.lastName,
        lastName,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.referralCode,
        referralCode,
      );
      // Mark step 2 as completed
      this.completedSteps.add(2);
      this.showForm(4);
    },

    async CheckVatForm({countryCode = '', vatNumber = ''}) {
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.countryCode,
        countryCode,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.vatNumber,
        vatNumber,
      );
      this.isCheckingVat = true;

      // Start timer to ensure minimum display time
      const startTime = Date.now();
      const minimumDisplayTime = 2500; // 2.5 seconds minimum

      try {
        let rights = null;

        if (countryCode === 'IN') {
          rights = await ApiService.checkIfIndiaVatNumberIsValid(
            this.myUser,
            countryCode,
            vatNumber,
            true,
          );
        } else {
          rights = await ApiService.checkIfVatNumberIsValid(
            this.myUser,
            countryCode,
            vatNumber,
            true,
          );
        }

        // The VAT number is correct
        if (rights) {
          Form.addContentInUserCompanyObject(
            this.myUser,
            enumValues.checkVatNumberBeforeAddAccount,
            null,
            false,
          );
        } else {
          // Force the value that will be used in the last form to know if we need to check this VAT n° before the creation
          Form.addContentInUserCompanyObject(
            this.myUser,
            enumValues.checkVatNumberBeforeAddAccount,
            null,
            true,
          );
        }
      } catch (error: any) {
        Form.addContentInUserCompanyObject(
          this.myUser,
          enumValues.checkVatNumberBeforeAddAccount,
          null,
          false,
        );
        MyLog.showLog(`Error: ${error.message} `);
      }

      // Ensure minimum display time has elapsed
      const elapsedTime = Date.now() - startTime;
      const remainingTime = minimumDisplayTime - elapsedTime;

      if (remainingTime > 0) {
        // Wait for remaining time to reach minimum display duration
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      // Always disable checking state after minimum time
      this.isCheckingVat = false;

      // Force the access to next page
      // Mark step 4 as completed
      this.completedSteps.add(4);
      this.showForm(5);
    },
    checkCompanyInformationsForm(dataChild: Map<String, String>) {
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.vatNumber,
        `${dataChild.get('vatNumber')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.designationLegal,
        `${dataChild.get('designationLegal')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.street,
        `${dataChild.get('street')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.houseNumber,
        `${dataChild.get('houseNumber')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.boxNumber,
        `${dataChild.get('boxNumber')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.postCode,
        `${dataChild.get('postCode')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.city,
        `${dataChild.get('city')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.billingEmail,
        `${dataChild.get('billingEmail')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.phone,
        `${dataChild.get('phone')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.mobile,
        `${dataChild.get('mobile')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.userSystemLanguage,
        `${dataChild.get('userLanguageSystem')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.activity,
        `${dataChild.get('activity')}`,
      );
      Form.addContentInUserCompanyObject(
        this.myUser,
        enumValues.referralCode,
        `${dataChild.get('referralCode')}`,
      );
      // Mark step 5 as completed
      this.completedSteps.add(5);
      this.showForm(6);
    },
    checkRightsForm(checked = true) {
      this.myUser.rightsAccepted = checked;
      // Mark step 6 as completed
      this.completedSteps.add(6);
      this.showForm(7);
    },
    checkReceivedURL(
      receivedURL: string,
      sep: string,
      expectedIDSize: number,
    ): StateSignUP {
      const parts = receivedURL.split(sep);
      // Check if the URL ends with a separator
      if (receivedURL.endsWith(sep)) {
        return StateSignUP.notUidReceived;
      }
      // Check if the URL ends with a separator and contains at least one character afterward with the correct ID size
      else if (
        parts.length > 1 &&
        parts[parts.length - 1].length === expectedIDSize
      ) {
        this.uidCustomerReceived = parts[parts.length - 1];
        return StateSignUP.goodFormatUidReceived;
      } else {
        return StateSignUP.badFormatUidReceived;
      }
    },
    closeBanner() {
      this.showBanner = false;
    },

    /*************************************************
     *                      API
     *************************************************/

    // Get customer email from API
    async getEmailAddressFromAPI(uid: string, referralCode: string) {
      this.showBanner = false;
      try {
        await ApiService.getUserInfos(uid, referralCode).then((value) => {
          // CASE : problem during email address retrieving
          if (value === null) {
            this.showBanner = true;
            this.canGetCustomerEmail = false;
            this.showAnimationBanner = false;
            this.userRetrievedData = {};
            this.currentBannerMessageKey =
              'admin-ui.please-click-again-on-link-in-latest-email';
            this.textBanner = this.t(
              'admin-ui.please-click-again-on-link-in-latest-email',
            );
          }
          // CASE : the user e-mail address is currently updated in "confirmed"
          else {
            this.showAnimationBanner = false;
            this.showBanner = true;
            this.canGetCustomerEmail = true;
            this.userRetrievedData = value;
            this.currentBannerMessageKey =
              'admin-ui.mail-address-successfully-confirmed';
            this.textBanner = this.t(
              'admin-ui.mail-address-successfully-confirmed',
            );
          }
        });
      } catch (e: any) {
        this.showBanner = true;
        // CASE : problem with HTTP request
        this.canGetCustomerEmail = false;
        this.currentBannerMessageKey =
          'admin-ui.an-error-has-been-encountered-please-try-again';
        this.textBanner = this.t(
          'admin-ui.an-error-has-been-encountered-please-try-again',
        );
      }
    },
    updateBannerTextForLanguage() {
      // Update banner text based on current message key
      if (this.showBanner && this.currentBannerMessageKey) {
        this.textBanner = this.t(this.currentBannerMessageKey);
      }
    },
    getCurrentStepNumber() {
      if (this.stateListOfFormsToShow[2]) return 1;
      if (this.stateListOfFormsToShow[4]) return 2;
      if (this.stateListOfFormsToShow[5]) return 3;
      if (this.stateListOfFormsToShow[6]) return 4;
      if (this.stateListOfFormsToShow[7]) return 5;
      return 0;
    },
    getCurrentStepTitle() {
      if (this.stateListOfFormsToShow[2]) return this.t('admin-ui.identity');
      if (this.stateListOfFormsToShow[4])
        return this.t('admin-ui.vat-verification');
      if (this.stateListOfFormsToShow[5]) return this.t('admin-ui.company');
      if (this.stateListOfFormsToShow[6]) return this.t('admin-ui.conditions');
      if (this.stateListOfFormsToShow[7]) return this.t('admin-ui.summary');
      return '';
    },
    getProgressPercentage() {
      const currentStep = this.getCurrentStepNumber();
      return (currentStep / 5) * 100;
    },
    isStepActive(stepNumber: number) {
      return this.stateListOfFormsToShow[stepNumber] === true;
    },
    isStepCompleted(stepNumber: number) {
      // A step is completed if it's in our completedSteps set
      return this.completedSteps.has(stepNumber);
    },
    getStepIndex(stepNumber: number) {
      // Convert form number to step index (1-5)
      const stepMap: {[key: number]: number} = {
        2: 1, // ContactInformation = Step 1
        4: 2, // CompanyCheckForm = Step 2
        5: 3, // CompanyInformationsForm = Step 3
        6: 4, // RightsForm = Step 4
        7: 5, // SummaryForm = Step 5
      };
      return stepMap[stepNumber] || 0;
    },
    canGoToPreviousStep() {
      if (this.isAccountCreationInProgress) {
        return false;
      }
      const currentStep = this.getCurrentStepNumber();
      return currentStep > 1;
    },
    goToPreviousStep() {
      // Prevent navigation during VAT checking or account creation
      if (this.isCheckingVat || this.isAccountCreationInProgress) {
        return;
      }

      // Use the existing previousClick logic from the forms
      if (this.nbrCurrentShowToForm === 2) {
        // Cannot go back from first step
        return;
      } else if (this.nbrCurrentShowToForm === 4) {
        // Reset completion status for steps after step 2
        this.completedSteps.delete(4);
        this.completedSteps.delete(5);
        this.completedSteps.delete(6);
        this.showForm(2); // Go back to ContactInformation
      } else if (this.nbrCurrentShowToForm === 5) {
        // Reset completion status for steps after step 4
        this.completedSteps.delete(5);
        this.completedSteps.delete(6);
        this.showForm(4); // Go back to CompanyCheckForm
      } else if (this.nbrCurrentShowToForm === 6) {
        // Reset completion status for steps after step 5
        this.completedSteps.delete(6);
        this.showForm(5); // Go back to CompanyInformationsForm
      } else if (this.nbrCurrentShowToForm === 7) {
        // No completion status to reset for last step
        this.showForm(6); // Go back to RightsForm
      }
    },
    getStepTooltip(step: any) {
      const stepNames: {[key: number]: string} = {
        2: this.t('admin-ui.identity'),
        4: this.t('admin-ui.vat-verification'),
        5: this.t('admin-ui.company'),
        6: this.t('admin-ui.conditions'),
        7: this.t('admin-ui.summary'),
      };
      const stepNumber =
        Object.keys(stepNames).indexOf(step.number.toString()) + 1;
      return `${this.t('admin-ui.step')} ${stepNumber}: ${
        stepNames[step.number as keyof typeof stepNames]
      }`;
    },
    canGoToNextStep() {
      const currentStep = this.getCurrentStepNumber();
      // Steps 1-4 can go to next step, step 5 is the last one
      return currentStep < 5;
    },
    smartGoToNextStep() {
      // Prevent navigation during VAT checking
      if (this.isCheckingVat) {
        return;
      }

      // Use event-based validation instead of refs - much cleaner!
      if (this.nbrCurrentShowToForm === 2) {
        this.triggerContactValidation++;
        return;
      }

      if (this.nbrCurrentShowToForm === 4) {
        this.triggerVatValidation++;
        return;
      }

      if (this.nbrCurrentShowToForm === 5) {
        this.triggerCompanyValidation++;
        return;
      }

      if (this.nbrCurrentShowToForm === 6) {
        this.triggerRightsValidation++;
        return;
      }
    },
    // Event handlers for validation results
    handleContactValidationResult(result: {
      success: boolean;
      message?: string;
    }) {
      if (result.success) {
        // Validation passed, checkContactForm will be called by the component
        return;
      } else {
        // Show error message
        this.showBanner = true;
        this.textBanner =
          result.message || this.t('admin-ui.please-fill-all-required-fields');
        this.showAnimationBanner = false;
        setTimeout(() => (this.showBanner = false), 4000);
      }
    },
    handleVatValidationResult(result: {success: boolean; message?: string}) {
      if (result.success) {
        // Validation passed, CheckVatForm will be called by the component
        return;
      } else {
        // Show error message
        this.showBanner = true;
        this.textBanner =
          result.message || this.t('admin-ui.please-enter-a-valid-vat-number');
        this.showAnimationBanner = false;
        setTimeout(() => (this.showBanner = false), 4000);
      }
    },
    handleCompanyValidationResult(result: {
      success: boolean;
      message?: string;
    }) {
      if (result.success) {
        // Validation passed, checkCompanyInformationsForm will be called by the component
        return;
      } else {
        // Show error message
        this.showBanner = true;
        this.textBanner =
          result.message ||
          this.t('admin-ui.please-fill-all-required-fields-of-the-company');
        this.showAnimationBanner = false;
        setTimeout(() => (this.showBanner = false), 4000);
      }
    },
    handleRightsValidationResult(result: {success: boolean; message?: string}) {
      if (result.success) {
        // Validation passed, checkRightsForm will be called by the component
        return;
      } else {
        // Show error message
        this.showBanner = true;
        this.textBanner =
          result.message || this.t('admin-ui.please-accept-general-conditions');
        this.showAnimationBanner = false;
        setTimeout(() => (this.showBanner = false), 4000);
      }
    },
    getNextActionTitle() {
      if (this.nbrCurrentShowToForm === 2) {
        return this.t('admin-ui.continue-to-vat-verification');
      } else if (this.nbrCurrentShowToForm === 4) {
        return this.t('admin-ui.verify-vat-number');
      } else if (this.nbrCurrentShowToForm === 5) {
        return this.t('admin-ui.continue-to-conditions');
      } else if (this.nbrCurrentShowToForm === 6) {
        return this.t('admin-ui.accept-and-continue');
      }
      return this.t('admin-ui.next-step');
    },
    getNextActionLabel() {
      return this.t('admin-ui.next');
    },
  },
});
</script>

<style scoped lang="scss">
/* Modern Progress Header with Floating Effect */
.progress-header {
  background: #f1f5f9;
  padding: 16px 20px;
  position: relative;
  z-index: 100;
}

.progress-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.95);
  padding: 16px 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Navigation Button Left */
.nav-button {
  background: linear-gradient(135deg, #0d3749 0%, #13526d 100%);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(13, 55, 73, 0.2);
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 20px;
  min-width: 100px;
  white-space: nowrap;
}

.nav-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 55, 73, 0.3);
  background: linear-gradient(135deg, #13526d 0%, #1d6b91 100%);
}

.nav-button:active {
  transform: translateY(0px);
}

/* Progress Track */
.progress-track {
  flex: 1;
  height: 6px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  max-width: 300px;
  margin: 0 auto;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00997c 0%, #1abc9c 100%);
  border-radius: 10px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 0 10px 10px 0;
}

/* Step Indicators */
.step-indicators {
  display: flex;
  gap: 8px;
  align-items: center;
}

.step-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e5e7eb;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.step-dot.active {
  background: linear-gradient(135deg, #00997c 0%, #1abc9c 100%);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 153, 124, 0.3);
}

.step-dot.completed {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.step-number {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
}

.step-dot.active .step-number {
  color: white;
}

.check-mark {
  font-size: 12px;
  color: white;
  font-weight: bold;
}

/* Current Step Info */
.current-step-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  white-space: nowrap;
}

.step-text {
  color: #374151;
  font-weight: 500;
}

.step-counter {
  background: #f3f4f6;
  color: #6b7280;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* Header Section */
.header-section {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

/* Content Section */
.content-section {
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .progress-header {
    padding: 12px 16px;
  }

  .progress-container {
    gap: 16px;
    padding: 12px 16px;
    border-radius: 12px;
    margin: 0 8px;
  }

  .progress-track {
    max-width: 250px;
    height: 5px;
  }

  .step-indicators {
    gap: 6px;
  }

  .step-dot {
    width: 20px;
    height: 20px;
  }

  .step-number {
    font-size: 9px;
  }

  .check-mark {
    font-size: 10px;
  }

  .current-step-info {
    font-size: 13px;
    gap: 6px;
  }

  .step-text {
    display: none; /* Hide step text on mobile to save space */
  }

  .step-counter {
    font-size: 11px;
    padding: 2px 6px;
  }

  /* Hide navigation button text on mobile, keep only icons */
  .nav-text {
    display: none;
  }

  .nav-button,
  .action-button {
    padding: 8px !important;
    min-width: 40px !important;
    width: 40px !important;
    height: 40px !important;
    border-radius: 50% !important;
    font-size: 13px;
  }

  .nav-button .nav-icon,
  .action-button .nav-icon {
    margin: 0 !important;
  }
}

@media (max-width: 480px) {
  .progress-container {
    gap: 12px;
    padding: 10px 12px;
    margin: 0 12px;
    border-radius: 10px;
  }

  .progress-track {
    max-width: 180px;
    height: 4px;
  }

  .nav-button,
  .action-button {
    padding: 6px !important;
    min-width: 36px !important;
    width: 36px !important;
    height: 36px !important;
    font-size: 12px;
  }
}

/* Dynamic Action Button */
.action-button {
  background: linear-gradient(135deg, #00997c 0%, #1abc9c 100%);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 153, 124, 0.2);
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 20px;
  min-width: 100px;
  white-space: nowrap;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 153, 124, 0.3);
  background: linear-gradient(135deg, #1abc9c 0%, #00b391 100%);
}

.action-button:active {
  transform: translateY(0px);
}

/* Disabled button styles */
.nav-button.disabled,
.action-button.disabled,
.nav-button:disabled,
.action-button:disabled {
  background: #d1d5db !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  transform: none !important;
  box-shadow: none !important;
  opacity: 0.6 !important;
}

.nav-button.disabled:hover,
.action-button.disabled:hover,
.nav-button:disabled:hover,
.action-button:disabled:hover {
  background: #d1d5db !important;
  transform: none !important;
  box-shadow: none !important;
}
</style>
